'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import {
  Education as GraduationCap,
  Group as Users,
  Wallet,
  Report as ClipboardList,
  EventSchedule as CalendarClock,
} from '@carbon/icons-react'

import { ChromaText } from '@/components/ui/chroma-text'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { FEATURES } from '@content/schoolerp/features'

/**
 * The School ERP hero. The old one was a stock photo under black scrims with
 * white drop-shadowed type — nothing like the rest of the site. This one is
 * built from the site's own language: page-colour canvas, Roca Two headline
 * with the chroma fill, hairline surfaces, and a code-drawn product mock on
 * the right instead of a photograph — theme-aware, crisp at any DPI.
 *
 * Every string in the mock is authored copy from features.js (module titles,
 * categories, and heroStat figures) — the visual invents no claims. The
 * module marquee under the fold is the FEATURES list itself.
 */

const FadeIn = ({
  children,
  delay = 0,
  y = 20,
  className,
}: {
  children: React.ReactNode
  delay?: number
  y?: number
  className?: string
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
)

/* Authored figures reused in the mock — value/label pairs come verbatim from
   each module's heroStat in features.js. */
const byS = (slug: string) => FEATURES.find((f: any) => f.slug === slug)
const ADMISSION = byS('digital-admissions')
const FEES = byS('fee-finance')
const PARENT = byS('parent-portal')

/** Decorative admissions funnel — heights only, no axis, no invented numbers. */
const BARS = [34, 52, 44, 68, 58, 84, 72, 96, 64, 88, 78, 100]

export function Hero() {
  const feeStat = FEES?.heroStat?.[0]
  const parentStat = PARENT?.heroStat?.[0]

  return (
    <section className="relative w-full overflow-hidden bg-background">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 items-center gap-14 pb-16 pt-[clamp(3.5rem,9vh,5.5rem)] lg:grid-cols-[1.02fr_0.98fr] lg:gap-10 lg:pb-20">
          {/* ── Copy ── */}
          <div>
            <FadeIn delay={0.07}>
              {/* The h1 has to name the category before it makes a promise —
                  "Transforming Institutions Through Intelligent System"
                  contained none of the words this page is asked for. */}
              <h1 className="font-heading max-w-xl text-4xl/[1.12] font-normal tracking-tight text-foreground sm:text-5xl/[1.1] xl:text-[56px]/[1.08]">
                <ChromaText>
                  School &amp; College ERP Software{' '}
                  <span className="text-emerald-600 dark:text-primary-active">
                    Built for Indian Campuses
                  </span>
                </ChromaText>
              </h1>
            </FadeIn>

            <FadeIn delay={0.14}>
              <p className="mt-5 max-w-xl text-base leading-[1.7] text-muted-foreground sm:text-lg">
                From student admissions to staff payroll, {FEATURES.length}{' '}
                deeply connected modules run every school and college operation
                on one unified platform, with zero per-user fees and UDISE+ and
                APAAR reporting built in.
              </p>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button asChild size="xl">
                  <Link href="#demo">Get Started</Link>
                </Button>
                <Button asChild variant="outline" size="xl">
                  <Link href="/schoolerp#features">Explore Features</Link>
                </Button>
              </div>
            </FadeIn>
          </div>

          {/* ── Product mock — drawn in tokens, honest in both themes ── */}
          <FadeIn delay={0.18} y={28} className="relative">
            {/* Soft brand wash behind the frame */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-x-12 -inset-y-10 bg-[radial-gradient(55%_45%_at_62%_32%,hsl(var(--accent-green)/0.13),transparent_70%)]"
            />

            <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-background shadow-[0_28px_80px_-36px_hsl(var(--foreground)/0.35)]">
              {/* Window chrome */}
              <div className="flex items-center gap-1.5 border-b border-border/70 px-4 py-2.5">
                <span className="size-2 rounded-full bg-border" />
                <span className="size-2 rounded-full bg-border" />
                <span className="size-2 rounded-full bg-border" />
                <span className="ml-3 rounded-md border border-border/70 px-2.5 py-0.5 text-[11px] text-muted-foreground">
                  School ERP
                </span>
              </div>

              <div className="grid grid-cols-[44px_1fr]">
                {/* Module rail */}
                <div
                  aria-hidden
                  className="flex flex-col items-center gap-5 border-r border-border/70 py-5"
                >
                  <GraduationCap
                    size={16}
                    className="text-emerald-600 dark:text-primary-active"
                  />
                  <Users size={16} className="text-muted-foreground/60" />
                  <Wallet size={16} className="text-muted-foreground/60" />
                  <ClipboardList
                    size={16}
                    className="text-muted-foreground/60"
                  />
                  <CalendarClock
                    size={16}
                    className="text-muted-foreground/60"
                  />
                </div>

                {/* Admissions view */}
                <div className="p-4 sm:p-5">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/80">
                      {ADMISSION?.category ?? 'Smart Enrollment Hub'}
                    </p>
                    <h3 className="mt-0.5 text-sm font-semibold text-foreground">
                      {ADMISSION?.title ?? 'Admission Management'}
                    </h3>
                  </div>

                  {/* Funnel bars */}
                  <div
                    aria-hidden
                    className="mt-5 flex h-24 items-end gap-1.5 sm:h-28"
                  >
                    {BARS.map((h, i) => (
                      <div
                        key={i}
                        className={
                          h === 100
                            ? 'flex-1 rounded-sm bg-emerald-600 dark:bg-primary-active'
                            : 'flex-1 rounded-sm bg-emerald-600/15 dark:bg-primary-active/15'
                        }
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>

                  {/* Sibling-module stat cards */}
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {feeStat && (
                      <div className="rounded-lg border border-border/70 p-3">
                        <p className="truncate text-[11px] text-muted-foreground">
                          {FEES?.title}
                        </p>
                        <p className="font-heading mt-1 text-xl text-foreground">
                          <AnimatedNumber value={feeStat.value} delay={150} />
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {feeStat.label}
                        </p>
                      </div>
                    )}
                    {parentStat && (
                      <div className="rounded-lg border border-border/70 p-3">
                        <p className="truncate text-[11px] text-muted-foreground">
                          {PARENT?.title}
                        </p>
                        <p className="font-heading mt-1 text-xl text-foreground">
                          <AnimatedNumber
                            value={parentStat.value}
                            delay={300}
                          />
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {parentStat.label}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ── Module marquee — the platform's breadth, verbatim ── */}
      <div className="relative border-y border-border/70">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-background to-transparent sm:w-28"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-background to-transparent sm:w-28"
        />
        <div className="flex overflow-hidden py-3.5">
          {[0, 1].map((copy) => (
            <ul
              key={copy}
              aria-hidden={copy === 1}
              className="flex w-max shrink-0 items-center motion-safe:animate-marquee motion-safe:[animation-duration:70s]"
            >
              {FEATURES.map((f: any) => (
                <li
                  key={f.slug}
                  className="flex items-center whitespace-nowrap text-[13px] text-muted-foreground"
                >
                  <span className="px-5">{f.title}</span>
                  <span
                    aria-hidden
                    className="size-1 rounded-full bg-emerald-600/50 dark:bg-primary-active/50"
                  />
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>
    </section>
  )
}
